import i18n from 'i18n';

/**
 * The stable codes api-issues returns in the `enum` field of a saved filter
 * error body (its `src/domains/issueTrackerFilter/issueTrackerFilter.errors.ts`),
 * mapped to the translation key holding the user-facing copy for that case.
 *
 * This is the contract. The accompanying `message` is English prose the API is
 * free to reword; matching on it, as this module used to have to, made every
 * such rewording a breaking change here.
 */
const TRANSLATION_KEY_BY_API_CODE = new Map<string, string>([
	['SAVED_FILTER_DUPLICATE_NAME', 'error.issuePage.customFilter.duplicateName'],
	['SAVED_FILTER_NAME_REQUIRED', 'error.issuePage.customFilter.nameRequired'],
	['SAVED_FILTER_NOT_FOUND', 'error.issuePage.customFilter.notFound'],
]);

/**
 * Message-based fallback, for responses that carry no code.
 *
 * Two entries are needed per case for a while yet. A browser session holding
 * this build can be talking to an api-issues that has not rolled out the codes
 * yet, and the pre-rollout API answers with the old wording — including two
 * ungrammatical literals ("Can't Found Targeted Issue Tracker Filter", "Saved
 * filter is not exists"). Dropping those strands those users on the generic
 * message until they reload against the new API.
 *
 * Once api-issues >= the release carrying `issueTrackerFilter.errors.ts` is in
 * production everywhere, the four pre-rollout literals below can go.
 */
const TRANSLATION_KEY_BY_API_MESSAGE = new Map<string, string>([
	// Post-rollout wording.
	['Filter with the same name already exists', 'error.issuePage.customFilter.duplicateName'],
	['Filter name is required', 'error.issuePage.customFilter.nameRequired'],
	['Saved filter not found', 'error.issuePage.customFilter.notFound'],
	// Pre-rollout wording, kept for deploy skew.
	['IssueTrackerFilter name is required', 'error.issuePage.customFilter.nameRequired'],
	["Can't Found Targeted Issue Tracker Filter", 'error.issuePage.customFilter.notFound'],
	['Saved filter is not exists', 'error.issuePage.customFilter.notFound'],
]);

const FALLBACK_TRANSLATION_KEY = 'error.issuePage.customFilter.generic';

/**
 * An error from a saved filter endpoint, carrying the API's machine-readable
 * code alongside its message.
 *
 * `issues.service` throws this so the code survives the trip to the component's
 * catch block; a plain `Error` or a bare string loses it.
 */
export class SavedFilterApiError extends Error {
	public readonly code?: string;

	public constructor(message: string, code?: string) {
		super(message);
		this.name = 'SavedFilterApiError';
		this.code = typeof code === 'string' ? code : undefined;
	}
}

/**
 * Reads the API's error code out of a caught value, when there is one.
 *
 * @param error - The value a `catch` block received.
 * @returns The code, or an empty string when the value carries none.
 */
function extractCode(error: unknown): string {
	if (error instanceof SavedFilterApiError && typeof error.code === 'string') {
		return error.code.trim();
	}

	return '';
}

/**
 * Reads the message out of a caught value.
 *
 * The saved filter calls fail in two shapes: `helpers/api`'s `API.post` throws
 * an `Error`, while `issues.service` throws the bare `json.message` string on
 * the update and delete paths. Anything else has no message to read.
 *
 * @param error - The value a `catch` block received.
 * @returns The trimmed message, or an empty string when there is none.
 */
function extractMessage(error: unknown): string {
	if (error instanceof Error) {
		return error.message.trim();
	}

	if (typeof error === 'string') {
		return error.trim();
	}

	return '';
}

/**
 * Resolves a caught saved-filter error into localized toast copy.
 *
 * Prefers the API's machine-readable code and falls back to matching the
 * message, so a response from an api-issues that predates the codes still
 * resolves. Never returns the raw API message: an unrecognized or absent
 * message yields the generic localized fallback.
 *
 * @param error - The value a `catch` block received.
 * @returns Localized copy safe to hand to `toast.error`.
 */
export function resolveSavedFilterErrorMessage(error: unknown): string {
	const key =
		TRANSLATION_KEY_BY_API_CODE.get(extractCode(error)) ??
		TRANSLATION_KEY_BY_API_MESSAGE.get(extractMessage(error)) ??
		FALLBACK_TRANSLATION_KEY;

	return i18n.t(key);
}
